import { useContext, useCallback, useRef } from "react";
import { AuthContext } from "../Auth/AuthContext";
import useLogger from "../Logger/useLogger";
import { AuthState } from "../types/AuthProvider";
import {
  getAuthHeaders,
  handleAuthError,
  shouldSkipErrorHandling,
} from "./utils/authUtils";
import { apiConfig } from "./config";

/**
 * Configuration for the useFetchClient hook
 */
export interface UseFetchClientConfig {
  baseURL?: string;
  timeout?: number;
  headers?: Record<string, string>;
  domain?: string;
}

/**
 * Enhanced fetch function with authentication and error handling
 */
export type FetchClient = (
  endpoint: string,
  options?: RequestInit
) => Promise<Response>;

/**
 * Custom hook that creates a fetch client with authentication
 *
 * Automatically adds:
 * - JWT Bearer token (from AWS Cognito)
 * - X-Account header (from AuthContext for multi-tenancy)
 * - X-Account header (from impersonation context if active)
 * - x-impersonate: "true" header (when impersonation context is active)
 * - Error handling (401/403 logout/stopImpersonation)
 * - Error logging (aligned with Apollo Client and Axios)
 *
 * @param config - Configuration object with baseURL and optional settings
 * @returns {FetchClient} Fetch function with JWT and X-Account authentication
 */
const useFetchClient = (config: UseFetchClientConfig = {}): FetchClient => {
  const { authState, logout, stopImpersonation } = useContext(AuthContext);
  const { logWarning, logError } = useLogger();

  // AuthContext is a js file so no inferred type
  const authRef = useRef<AuthState>(authState as AuthState);
  authRef.current = authState as AuthState;

  // Use provided baseURL or default to apiConfig.REST_API_URL
  const baseURL = config.baseURL || apiConfig.REST_API_URL;

  const fetchClient = useCallback<FetchClient>(
    async (endpoint: string, options: RequestInit = {}) => {
      let opId: string | undefined;
      // Tracks whether the request was aborted by the internal TTFB timeout
      // (versus a caller abort or another network failure). Declared at the
      // outer scope so the catch block below can branch on it.
      let didTimeout = false;
      try {
        // Get authenticated headers (JWT + X-Account)
        const authHeaders = await getAuthHeaders(authRef, {
          "Content-Type": "application/json",
          ...config.headers,
          ...(options.headers as Record<string, string>),
        });
        opId = authHeaders["op-id"] as string;

        // Build full URL
        const resolvedBaseURL = config.domain
          ? new URL(config.domain, baseURL).href
          : baseURL;
        const url = `${resolvedBaseURL}${endpoint}`;

        // Make fetch request with timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          didTimeout = true;
          controller.abort();
        }, config.timeout || 30000);

        try {
          const response = await fetch(url, {
            ...options,
            headers: authHeaders,
            signal: controller.signal,
          });

          clearTimeout(timeoutId);

          // Handle error responses (aligned with Axios interceptor)
          if (!response.ok) {
            const status = response.status;

            // Skip error handling in dev/localhost/amplify (same as Axios)
            if (!shouldSkipErrorHandling()) {
              // 1. Log warning for all errors
              logWarning(
                new Error(`HTTP ${status}`),
                `REST API Error: ${options.method || "GET"} ${url}`,
                {
                  tags: {
                    url,
                    method: options.method || "GET",
                    status: status.toString(),
                  },
                  extra: {
                    url,
                    method: options.method || "GET",
                    status,
                  },
                }
              );

              // 2. Handle 401/403 (same as Axios)
              if (status) {
                let responseData: unknown;
                try {
                  responseData = await response.clone().json();
                } catch {
                  responseData = undefined;
                }
                handleAuthError(
                  status,
                  authRef,
                  logout,
                  stopImpersonation,
                  responseData
                );
              }

              // 3. Log error for 401/403 or 5xx
              if (status === 401 || status === 403 || status >= 500) {
                logError(
                  new Error(`HTTP ${status}`),
                  `REST API Error: ${options.method || "GET"} ${url}`,
                  {
                    tags: {
                      url,
                      method: options.method || "GET",
                      status: status.toString(),
                      type:
                        status === 401 || status === 403 ? "401/403" : "5xx",
                      opId,
                    },
                    extra: {
                      url,
                      method: options.method || "GET",
                      status,
                      statusText: response.statusText,
                    },
                  }
                );
              }
            }
          }

          // Throw on error response (same as Axios behavior)
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          return response;
        } finally {
          clearTimeout(timeoutId);
        }
      } catch (error) {
        // Handle network errors and timeouts
        if (!shouldSkipErrorHandling()) {
          const resolvedBaseURL = config.domain
            ? new URL(config.domain, baseURL).href
            : baseURL;
          const url = `${resolvedBaseURL}${endpoint}`;

          // Distinguish internal TTFB timeout (before first byte) from other
          // network errors so it can be filtered separately in Sentry.
          // `didTimeout` is set in the timeout callback above just before
          // `controller.abort()`. When the abort fires, fetch rejects with an
          // AbortError, which we detect via name/code.
          const isError = error instanceof Error;
          const isAbortError =
            isError &&
            (error.name === "AbortError" || error.name === "TimeoutError");
          const isTimeoutAbort = isAbortError && didTimeout;
          const isSseRequest =
            (options.headers as Record<string, string> | undefined)?.Accept === "text/event-stream";
          const errorMessage = isError ? error.message : undefined;

          if (isTimeoutAbort && isSseRequest) {
            logError(
              error,
              "SSE connection timeout: failed to establish SSE connection",
              {
                tags: {
                  url,
                  method: options.method || "GET",
                  type: "sse_timeout",
                  opId,
                },
                extra: {
                  url,
                  method: options.method || "GET",
                  timeoutMs: config.timeout || 30000,
                  errorMessage,
                },
              }
            );
          } else if (isTimeoutAbort) {
            logError(
              error,
              `REST API timeout: ${options.method || "GET"} ${url}`,
              {
                tags: {
                  url,
                  method: options.method || "GET",
                  type: "timeout",
                  opId,
                },
                extra: {
                  url,
                  method: options.method || "GET",
                  timeoutMs: config.timeout || 30000,
                  errorMessage,
                },
              }
            );
          } else {
            logError(
              error,
              `REST API Network Error: ${options.method || "GET"} ${url}`,
              {
                tags: {
                  url,
                  method: options.method || "GET",
                  type: "network",
                  opId,
                },
                extra: {
                  url,
                  method: options.method || "GET",
                  errorMessage,
                },
              }
            );
          }
        }

        throw error;
      }
    },
    [
      baseURL,
      config.timeout,
      config.headers,
      logout,
      stopImpersonation,
      logWarning,
      logError,
    ]
  );

  return fetchClient;
};

export default useFetchClient;
